/**
 * „Doprava" a „Termíny" ke kontrole přímo v průvodci (KAN-1837).
 *
 * Průvodce dřív ukazoval jen jména dopravců; ceny pásem a rychlosti doručení
 * se zapsaly ze šablony platformy a Rozjezd pak nad nimi visel na „ke kontrole",
 * protože je nikdo neviděl. Tady se ukážou přesně ta čísla, která seed zapíše,
 * s možností je přepsat. Že byla ukázaná, se hlásí ven (``onShown``) — jen
 * to, co tenant opravdu viděl, backend potvrdí.
 */

import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { IngotIcon, IngotTable } from "@/ingot";
import { majorToMinor, minorToMajor } from "@/lib/money";
import { formatDecimal } from "@/lib/utils";

import type {
  WizardCarrier,
  WizardCarrierRate,
  WizardDeliveryTier,
  WizardTierValues,
} from "./model";

/** Klíč pásma shodný s tím, který backend páruje (dopravce / země / hmotnost). */
export function rateKey(
  carrierCode: string,
  countryCode: string,
  maxWeightKg: number,
): string {
  return `${carrierCode}/${countryCode}/${maxWeightKg}`;
}

/** Číslo z políčka; prázdné nebo záporné není odpověď. */
function parseNumber(raw: string): number | null {
  const value = Number(raw.replace(",", "."));
  return raw.trim() !== "" && Number.isFinite(value) && value >= 0
    ? value
    : null;
}

function NumberCell({
  value,
  onCommit,
  label,
  testId,
  step = "any",
}: {
  value: number;
  onCommit: (next: number) => void;
  label: string;
  testId: string;
  step?: string;
}) {
  return (
    <input
      type="number"
      className="input"
      min={0}
      step={step}
      aria-label={label}
      data-testid={testId}
      defaultValue={value}
      onChange={(e) => {
        const parsed = parseNumber(e.target.value);
        if (parsed !== null) onCommit(parsed);
      }}
      style={{ width: 110 }}
    />
  );
}

export function DeliveryReview({
  carriers,
  carrierCodes,
  rateOverrides,
  onRateChange,
  tiers,
  tierOverrides,
  onTierChange,
  onShown,
  countryName,
  numbers,
  transitDays,
  heaviestKg,
  personalPickup,
  lang,
}: {
  carriers: WizardCarrier[];
  carrierCodes: string[];
  rateOverrides: Record<string, number>;
  onRateChange: (key: string, priceMinor: number) => void;
  /** Prázdné = obchod bez operací (nebo se úrovně nenačetly): nic se neukáže. */
  tiers: WizardDeliveryTier[];
  tierOverrides: Record<string, WizardTierValues>;
  onTierChange: (code: string, values: WizardTierValues) => void;
  onShown: (kind: "shipping" | "tiers") => void;
  countryName: (code: string) => string;
  /** KAN-2015 — pořadí sekcí v kroku (číslují se průběžně). */
  numbers: { shipping: number; tiers: number };
  /** Nejrychlejší doprava pokladny do první země (``null`` = neznámá). */
  transitDays: number | null;
  /** Nejtěžší zásilka, kterou vybraní dopravci uvezou (``null`` = žádné pásmo). */
  heaviestKg: number | null;
  personalPickup: boolean;
  lang: string;
}) {
  const { t } = useTranslation("admin");
  const kg = (value: number) => formatDecimal(value, lang);
  const numbered = (n: number, title: string) =>
    t("onboardingStart.sectionNumbered", { n, title });
  const picked = carriers.filter(
    (c) => carrierCodes.includes(c.code) && (c.rates?.length ?? 0) > 0,
  );
  const showShipping = picked.length > 0;
  const showTiers = tiers.length > 0;
  const valuesOf = (tier: WizardDeliveryTier): WizardTierValues =>
    tierOverrides[tier.code] ?? {
      base_min_days: tier.base_min_days,
      multiplier: tier.multiplier,
      flat_surcharge_minor: tier.flat_surcharge_minor,
    };

  useEffect(() => {
    if (showShipping) onShown("shipping");
  }, [showShipping, onShown]);
  useEffect(() => {
    if (showTiers) onShown("tiers");
  }, [showTiers, onShown]);

  return (
    <>
      {showShipping ? (
        <section className="opblock" data-testid="onboarding-shipping-review">
          <header className="opblock-head">
            <h3>
              {numbered(numbers.shipping, t("onboardingStart.shippingReview.title"))}
            </h3>
          </header>
          <div className="opblock-body">
            <p className="q-sub" style={{ marginBottom: 12 }}>
              {t("onboardingStart.shippingReview.lead")}
            </p>
            {heaviestKg !== null ? (
              // KAN-2015 — co se stane s objednávkou, kterou dopravci
              // neuvezou: pokladna jí dopravu nenabídne (KAN-1825) a zákazník
              // pošle poptávku, případně si ji vyzvedne. Plechy, desky a
              // svařence tu hranici snadno přelezou, tak se to říká předem.
              <div
                className={personalPickup ? "zero" : "zero zero-warn"}
                style={{ marginBottom: 12 }}
                data-testid="onboarding-heavy-shipments"
              >
                <IngotIcon name="info" size={18} />
                <div>
                  <b>
                    {t("onboardingStart.shippingReview.heavyTitle", {
                      kg: kg(heaviestKg),
                    })}
                  </b>
                  <div className="t-small">
                    {t(
                      personalPickup
                        ? "onboardingStart.shippingReview.heavyWithPickup"
                        : "onboardingStart.shippingReview.heavyNoPickup",
                    )}
                  </div>
                </div>
              </div>
            ) : null}
            {picked.map((carrier) => (
              <div key={carrier.code} style={{ marginBottom: 16 }}>
                <IngotTable
                  testId={`onboarding-rates-${carrier.code}`}
                  caption={carrier.name}
                  columns={[
                    {
                      key: "country",
                      header: t("onboardingStart.shippingReview.country"),
                      cell: (band: WizardCarrierRate) =>
                        countryName(band.country_code),
                    },
                    {
                      key: "weight",
                      header: t("onboardingStart.shippingReview.weight"),
                      cell: (band: WizardCarrierRate) =>
                        t("onboardingStart.shippingReview.upTo", {
                          kg: kg(band.max_weight_kg),
                        }),
                    },
                    {
                      key: "price",
                      header: t("onboardingStart.shippingReview.price"),
                      cell: (band: WizardCarrierRate) => {
                        const key = rateKey(
                          carrier.code,
                          band.country_code,
                          band.max_weight_kg,
                        );
                        return (
                          <>
                            <NumberCell
                              value={minorToMajor(
                                rateOverrides[key] ?? band.price_minor,
                                band.currency_code,
                              )}
                              label={t(
                                "onboardingStart.shippingReview.priceLabel",
                                {
                                  carrier: carrier.name,
                                  country: countryName(band.country_code),
                                  kg: kg(band.max_weight_kg),
                                },
                              )}
                              testId={`onboarding-rate-${key}`}
                              onCommit={(next) =>
                                onRateChange(
                                  key,
                                  majorToMinor(next, band.currency_code),
                                )
                              }
                            />{" "}
                            {band.currency_code}
                          </>
                        );
                      },
                    },
                  ]}
                  rows={carrier.rates ?? []}
                  rowKey={(band: WizardCarrierRate) =>
                    rateKey(carrier.code, band.country_code, band.max_weight_kg)
                  }
                />
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {showTiers ? (
        <section className="opblock" data-testid="onboarding-tiers-review">
          <header className="opblock-head">
            <h3>
              {numbered(numbers.tiers, t("onboardingStart.tiersReview.title"))}
            </h3>
          </header>
          <div className="opblock-body">
            <p className="q-sub" style={{ marginBottom: 12 }}>
              {t("onboardingStart.tiersReview.lead")}
            </p>
            <IngotTable
              testId="onboarding-tiers"
              caption={t("onboardingStart.tiersReview.title")}
              columns={[
                {
                  key: "name",
                  header: t("onboardingStart.tiersReview.name"),
                  cell: (tier: WizardDeliveryTier) => tier.label,
                },
                {
                  key: "days",
                  header: t("onboardingStart.tiersReview.days"),
                  cell: (tier: WizardDeliveryTier) => (
                    <>
                      <NumberCell
                        value={valuesOf(tier).base_min_days}
                        step="1"
                        label={t("onboardingStart.tiersReview.daysLabel", {
                          name: tier.label,
                        })}
                        testId={`onboarding-tier-days-${tier.code}`}
                        onCommit={(next) =>
                          onTierChange(tier.code, {
                            ...valuesOf(tier),
                            base_min_days: Math.round(next),
                          })
                        }
                      />{" "}
                      {t(
                        tier.day_basis === "business"
                          ? "onboardingStart.tiersReview.businessDaysUnit"
                          : "onboardingStart.tiersReview.calendarDaysUnit",
                        { count: valuesOf(tier).base_min_days },
                      )}
                    </>
                  ),
                },
                {
                  // KAN-2015 — co zákazník uvidí: výroba + nejrychlejší
                  // doprava pokladny. Web obchodu („Express od N dnů") i
                  // pokladna počítají totéž.
                  key: "delivery",
                  header: t("onboardingStart.tiersReview.delivery"),
                  cell: (tier: WizardDeliveryTier) => (
                    <span data-testid={`onboarding-tier-delivery-${tier.code}`}>
                      {transitDays === null
                        ? t("onboardingStart.tiersReview.deliveryUnknown")
                        : t("onboardingStart.tiersReview.deliveryFrom", {
                            count: valuesOf(tier).base_min_days + transitDays,
                            transit: t("onboardingStart.tiersReview.transit", {
                              count: transitDays,
                            }),
                          })}
                    </span>
                  ),
                },
                {
                  // KAN-2015 — násobek 1,25 je pro majitele „+25 % k ceně
                  // výroby"; ukládá se dál jako násobek.
                  key: "multiplier",
                  header: t("onboardingStart.tiersReview.multiplier"),
                  cell: (tier: WizardDeliveryTier) => (
                    <>
                      <NumberCell
                        value={Math.round((valuesOf(tier).multiplier - 1) * 1000) / 10}
                        label={t("onboardingStart.tiersReview.multiplierLabel", {
                          name: tier.label,
                        })}
                        testId={`onboarding-tier-multiplier-${tier.code}`}
                        onCommit={(pct) =>
                          onTierChange(tier.code, {
                            ...valuesOf(tier),
                            multiplier: Math.round((1 + pct / 100) * 10000) / 10000,
                          })
                        }
                      />{" "}
                      {t("onboardingStart.tiersReview.multiplierUnit")}
                    </>
                  ),
                },
                {
                  key: "surcharge",
                  header: t("onboardingStart.tiersReview.surcharge"),
                  cell: (tier: WizardDeliveryTier) => (
                    <>
                      <NumberCell
                        value={minorToMajor(
                          valuesOf(tier).flat_surcharge_minor,
                          tier.currency_code,
                        )}
                        label={t("onboardingStart.tiersReview.surchargeLabel", {
                          name: tier.label,
                        })}
                        testId={`onboarding-tier-surcharge-${tier.code}`}
                        onCommit={(next) =>
                          onTierChange(tier.code, {
                            ...valuesOf(tier),
                            flat_surcharge_minor: majorToMinor(
                              next,
                              tier.currency_code,
                            ),
                          })
                        }
                      />{" "}
                      {tier.currency_code}
                    </>
                  ),
                },
              ]}
              rows={tiers}
              rowKey={(tier: WizardDeliveryTier) => tier.code}
            />
          </div>
        </section>
      ) : null}
    </>
  );
}
